'use client';

import firebase from 'firebase/compat/app';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { db } from '../config/firebaseConfig';
import { UserAuth } from '../context/authContext';
import DashboardLayout from './layout/DashboardLayout';
import CopyComponent from './utils/CopyComponent';

const formatAddress = (address) => {
  if (!address) return '';
  return `${address.substring(0, 8)}...${address.substring(
    address.length - 6
  )}`;
};

export default function Participants() {
  const { userData, user } = UserAuth();
  const [roomId, setRoomId] = useState(localStorage.getItem('roomId') || '');
  const [datax, setDatax] = useState('');
  const [createdBy, setCreatedBy] = useState([]);

  useEffect(() => {
    if (!roomId) return;

    const unsubscribe = db
      .collection('rooms')
      .doc(roomId)
      .onSnapshot(
        (roomSnapshot) => {
          if (roomSnapshot.exists) {
            const data = roomSnapshot.data();
            setDatax(data);
            setCreatedBy(data.createdBy || []);
          } else {
            localStorage.removeItem('roomId');
            window.location.reload();
          }
        },
        (error) => {
          console.error('Error fetching room details:', error);
          toast.error('Error fetching room details.');
        }
      );

    return () => unsubscribe();
  }, [roomId]);

  const [role, setRole] = useState(() => localStorage.getItem('role') || '');

  const handleLeaveRoom = async () => {
    try {
      await db
        .collection('rooms')
        .doc(roomId)
        .update({
          [`${role}s`]: firebase.firestore.FieldValue.arrayRemove(
            userData.publicKey
          ),
        });
      localStorage.removeItem('roomId');
      localStorage.removeItem('role');
      toast.success('You have left the room successfully.');
      window.location.href = '/';
      setRoomId('');
      setRole('');
    } catch (error) {
      console.error('Error leaving room:', error);
      toast.error('Failed to leave room. Please try again later.');
    }
  };

  const handleEndRoom = async () => {
    if (roomId && user.email) {
      try {
        const roomDocRef = db.collection('rooms').doc(roomId);

        const subcollectionNames = ['blocks', 'transactions'];

        await Promise.all(
          subcollectionNames.map(async (subcollectionName) => {
            const subcollectionRef = roomDocRef.collection(subcollectionName);
            const docs = await subcollectionRef.get();
            docs.forEach(async (doc) => {
              await doc.ref.delete();
            });
          })
        );

        await roomDocRef.delete();

        toast.success('Room ended successfully.');
        window.location.href = '/';

        setRoomId('');
        localStorage.removeItem('roomId');
      } catch (error) {
        console.error('Error ending room:', error);
        toast.error('Failed to end room. Please try again later.');
      }
    } else {
      toast.error('Cannot end room: Room ID or user information is missing.');
    }
  };

  if (!roomId) {
    return (
      <DashboardLayout>
        <section className="min-h-screen pt-12 pb-8 px-6 flex flex-col items-center justify-center">
          <div className="max-w-md w-full">
            <div className="bg-white/5 backdrop-blur-2xl rounded-2xl p-8 border border-white/5 text-center shadow-2xl shadow-black/20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/10 ring-4 ring-blue-500/5">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-blue-400"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Join a Room
              </h2>
              <p className="text-white/50 leading-relaxed mb-8">
                You need to be in an active room to view the participants list
                and interactions.
              </p>
              <a
                href="/"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors w-full"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        </section>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <section className="min-h-screen pt-10 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                Lobby
                <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide uppercase">
                  Live
                </span>
              </h1>
              <p className="text-white/40 mt-1">
                Manage and view all active users in this room
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Controls/Actions can go here if needed later */}
            </div>
          </div>

          {/* Main Grid Layout to fix 'Left Heavy' issue */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <ListofParticipant roomId={roomId} />
            <ListMiner roomId={roomId} />
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}

const ListofParticipant = ({ roomId }) => {
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    if (!roomId) return;
    const unsubscribe = db
      .collection('rooms')
      .doc(roomId)
      .onSnapshot(async (doc) => {
        if (doc.exists) {
          const roomData = doc.data();
          if (roomData.participants?.length > 0) {
            const users = await getDocs(
              query(
                collection(db, 'users'),
                where('publicKey', 'in', roomData.participants)
              )
            );
            setParticipants(users.docs.map((v) => v.data()));
          } else {
            setParticipants([]);
          }
        }
      });
    return () => unsubscribe();
  }, [roomId]);

  return (
    <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden flex flex-col h-full">
      <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-500/10 rounded-xl text-blue-400 border border-blue-500/10">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div>
            <h4 className="text-white font-semibold text-lg leading-tight">
              Participants
            </h4>
            <p className="text-white/30 text-xs">Standard users</p>
          </div>
        </div>
        <div className="px-3 py-1 rounded-full bg-white/5 border border-white/5 text-white/60 text-xs font-mono">
          {participants.length} Active
        </div>
      </div>

      <div className="p-4 space-y-2">
        {participants.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 mx-auto mb-3 text-white/10">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                ></path>
              </svg>
            </div>
            <p className="text-white/30 text-sm">No participants yet</p>
          </div>
        ) : (
          participants.map((participant, index) => (
            <div
              key={index}
              className="group flex items-center justify-between p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] hover:scale-[1.01] hover:shadow-lg hover:shadow-black/20 border border-transparent hover:border-white/10 transition-all duration-300 ease-out"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="relative">
                  <img
                    src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${participant.email}`}
                    alt="avatar"
                    className="w-11 h-11 rounded-full bg-white/5 ring-2 ring-white/5 group-hover:ring-white/10 transition-all"
                  />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#13151b]"></div>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-white/90 font-medium text-sm truncate mb-0.5 group-hover:text-blue-400 transition-colors">
                    {participant.email}
                  </p>
                  <p className="text-white/30 text-xs font-mono truncate tracking-wide">
                    {formatAddress(participant.publicKey)}
                  </p>
                </div>
              </div>
              <div className="opacity-60 group-hover:opacity-100 transition-opacity pl-4">
                <CopyComponent data={participant.publicKey} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const ListMiner = ({ roomId }) => {
  const [miners, setMiners] = useState([]);

  useEffect(() => {
    if (!roomId) return;
    const unsubscribe = db
      .collection('rooms')
      .doc(roomId)
      .onSnapshot(async (doc) => {
        if (doc.exists) {
          const roomData = doc.data();
          if (roomData.miners?.length > 0) {
            const users = await getDocs(
              query(
                collection(db, 'users'),
                where('publicKey', 'in', roomData.miners)
              )
            );
            setMiners(users.docs.map((v) => v.data()));
          } else {
            setMiners([]);
          }
        }
      });
    return () => unsubscribe();
  }, [roomId]);

  return (
    <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden flex flex-col h-full">
      <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/10 rounded-xl text-amber-400 border border-amber-500/10">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>
              <path d="M12 12v.01"></path>
            </svg>
          </div>
          <div>
            <h4 className="text-white font-semibold text-lg leading-tight">
              Validators
            </h4>
            <p className="text-white/30 text-xs">Block validators</p>
          </div>
        </div>
        <div className="px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-mono">
          {miners.length} Active
        </div>
      </div>

      <div className="p-4 space-y-2">
        {miners.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 mx-auto mb-3 text-white/10">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                ></path>
              </svg>
            </div>
            <p className="text-white/30 text-sm">No validators assigned</p>
          </div>
        ) : (
          miners.map((participant, index) => (
            <div
              key={index}
              className="group flex items-center justify-between p-3 rounded-xl bg-amber-500/[0.03] hover:bg-amber-500/[0.08] hover:scale-[1.01] hover:shadow-lg hover:shadow-amber-500/5 border border-transparent hover:border-amber-500/20 transition-all duration-300 ease-out"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="relative">
                  <img
                    src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${participant.email}`}
                    alt="avatar"
                    className="w-11 h-11 rounded-full bg-white/5 ring-2 ring-amber-500/20 group-hover:ring-amber-500/40 transition-all"
                  />
                  <div className="absolute -bottom-1 -right-1 bg-amber-500 rounded-full p-0.5 border-2 border-[#13151b] shadow-sm">
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      className="text-[#13151b]"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white/90 font-medium text-sm truncate mb-0.5 group-hover:text-amber-400 transition-colors">
                    {participant.email}
                  </p>
                  <p className="text-amber-500/50 text-xs font-mono truncate tracking-wide">
                    {formatAddress(participant.publicKey)}
                  </p>
                </div>
              </div>
              <div className="opacity-60 group-hover:opacity-100 transition-opacity pl-4">
                <CopyComponent data={participant.publicKey} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
